/**
 * AllInventoriesPage Container Component
 *
 * Stateless page container that composes section components.
 * State management is handled by the InventoryManagement parent.
 */

import { Counter } from '@/components/ui/counter';
import { useTranslation } from '@hooks/useTranslation';
import { DataTableShortcuts } from '../../DataTable/DataTableShortcuts';
import { InventoriesTabs } from '../Sections/InventoriesTabs';
import { InventoriesToolbar } from '../Sections/InventoriesToolbar';
import { InventoriesTable } from '../Sections/InventoriesTable';
import { PageTitle } from '../../layouts/Page';
import type { AllInventoriesPageProps } from '../types';

const DEFAULT_INVENTORIES: never[] = [];

export function AllInventoriesPage({
  currentStatus = 'active',
  totalCount = 0,
  onStatusChange,
  translations,
  isLoading = false,
  isFetching = false,
  inventories = DEFAULT_INVENTORIES,
  currentPage = 1,
  pageSize = 50,
  onPageChange,
  onPerPageChange,
  onRowClick,
  onCreateNew,
  canWrite = true,
  sorting,
  onSortingChange,
}: AllInventoriesPageProps) {
  const { t } = useTranslation();
  const countText = isLoading ? '…' : totalCount.toLocaleString();

  return (
    <DataTableShortcuts>
      <div className="flex flex-col gap-4 flex-1 min-h-0 px-6 sm:px-8 lg:px-16">
        <PageTitle>
          <h1 className="font-kapra-expanded text-2xl font-black uppercase leading-[1.5] text-off-black">
            {t('index.title')}
          </h1>
          <Counter value={countText} data-loading={isLoading || isFetching} />
        </PageTitle>

        <div className="flex flex-shrink-0 items-center justify-between gap-4">
          <InventoriesTabs
            currentStatus={currentStatus}
            onStatusChange={onStatusChange}
            translations={translations?.tabs}
          />
          <InventoriesToolbar onCreateNew={onCreateNew} canWrite={canWrite} className="hidden md:block" />
        </div>

        <InventoriesTable
          inventories={inventories}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          currentStatus={currentStatus}
          isLoading={isLoading}
          isFetching={isFetching}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
          onRowClick={onRowClick}
          sorting={sorting}
          onSortingChange={onSortingChange}
          className="flex-1 min-h-0"
        />
      </div>
    </DataTableShortcuts>
  );
}
